import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import AppLayout from './layout/AppLayout';
import Login from './pages/Login';
import Overview from './pages/Overview';
import AllServices from './pages/AllServices';
import ServiceDetail from './pages/ServiceDetail';
import InstanceDetail from './pages/InstanceDetail';
import Iam from './pages/Iam';
import Contracts from './pages/Contracts';
import PriceManagement from './pages/PriceManagement';
import ContractNew from './pages/ContractNew';
import ContractDetail from './pages/ContractDetail';
import Providers from './pages/Providers';
import ProviderDetail from './pages/ProviderDetail';
import ProviderRegions from './pages/ProviderRegions';
import ProviderCompartments from './pages/ProviderCompartments';
import ProviderPriceList from './pages/ProviderPriceList';
import ProviderPriceSyncHistory from './pages/ProviderPriceSyncHistory';
import Vaults from './pages/Vaults';
import VaultDetail from './pages/VaultDetail';
import Resources from './pages/Resources';
import Costs from './pages/Costs';
import Billing from './pages/Billing';
import Activity from './pages/Activity';
import Superadmin from './pages/Superadmin';
import KVMSettings from './pages/KVMSettings';
import QemuServers from './pages/QemuServers';
import NotFound from './pages/NotFound';

// RequireAuth gates the app shell: unauthenticated users are redirected to
// /login (remembering where they were headed).
function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="app-boot">Loading…</div>;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

// RequireFeature gates a console area that a policy has to grant. The sidebar
// already hides what it cannot reach, but Console home is the target of the
// brand and breadcrumb links and the superadmin's post-login landing page, so a
// member without the grant would otherwise arrive at a page whose every request
// 403s. A member's own landing (Billing) needs no guard: `landing` in
// AuthContext only names an area the account is granted.
function RequireFeature({ feature, children }) {
  const { allows } = useAuth();
  return allows(feature) ? children : <Navigate to="/services" replace />;
}

// LandingRedirect resolves the bare app URL to the page this account came for:
// Billing for a member, Console home for a superadmin (see `landing` in
// AuthContext). It is not a hardcoded destination because "/" is not a page — a
// member typing the app's bare address is asking for their own landing, the same
// as signing in with nothing else requested.
//
// This is also what makes that landing reachable at all in the common case. Coming
// to "/" logged out, RequireAuth stores "/" as the requested URL, and a requested
// URL wins over `landing` on the way back — so a hardcoded index would send every
// member who arrives the ordinary way to the service catalogue instead.
function LandingRedirect() {
  const { landing } = useAuth();
  return <Navigate to={landing} replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<LandingRedirect />} />
        <Route
          path="home"
          element={
            <RequireFeature feature="home">
              <Overview />
            </RequireFeature>
          }
        />
        <Route path="services" element={<AllServices />} />
        {/* Before the generic service route: an instance is addressed by its
            provider OCID, and only Compute has a detail read. */}
        <Route
          path="services/:providerId/compute/instances/:instanceId"
          element={<InstanceDetail />}
        />
        <Route path="services/:providerId/:serviceId" element={<ServiceDetail />} />
        <Route path="iam" element={<Navigate to="/iam/user-groups" replace />} />
        <Route
          path="iam/:section"
          element={
            <RequireFeature feature="iam">
              <Iam />
            </RequireFeature>
          }
        />
        <Route path="contracts" element={<Contracts />} />
        <Route path="contracts/new" element={<ContractNew />} />
        <Route path="contracts/:id" element={<ContractDetail />} />
        <Route path="prices" element={<PriceManagement />} />
        <Route path="providers" element={<Providers />} />
        <Route path="providers/:id" element={<ProviderDetail />} />
        <Route path="providers/:id/regions" element={<ProviderRegions />} />
        <Route
          path="providers/:id/compartments"
          element={<ProviderCompartments />}
        />
        <Route path="providers/:id/price-list" element={<ProviderPriceList />} />
        <Route
          path="providers/:id/price-list/history"
          element={<ProviderPriceSyncHistory />}
        />
        <Route path="vaults" element={<Vaults />} />
        <Route path="vaults/:vaultId" element={<VaultDetail />} />
        <Route path="resources" element={<Resources />} />
        <Route path="costs" element={<Costs />} />
        <Route path="billing" element={<Billing />} />
        <Route path="activity" element={<Activity />} />
        <Route path="superadmin" element={<Superadmin />} />
        <Route path="admin/kvm-settings" element={<KVMSettings />} />
        <Route path="admin/qemu-servers" element={<QemuServers />} />
        <Route path="404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Route>
    </Routes>
  );
}
